import { Box, Button } from '@amzn/awsui-components-core';
import { Content, ContentNode } from '@amzn/awscat-pattern-library-service-model';
import React, { useContext, useEffect, useState } from 'react';
import { deepClone } from '../../../api/UtilsApi';
import constants from '../../../common/constants';
import ModalDialogButtonV3 from '../../common/ModalDialogButtonV3';
import { ContentViewerContext } from '../ContentViewerContext';
import { ContentPagesTree, TreeStatus } from './ContentPagesTree';
import { findParentPage } from './FindParentPage';
import { ROOT_SECTION_ID } from '../../../model/Contents';

export const ReorderContentPageButton = () => {
  const viewerCtx = useContext(ContentViewerContext);

  const [updatedContent, setUpdatedContent] = useState<Content>(deepClone(viewerCtx.content));

  const [selectedPage, setSelectedPage] = useState<ContentNode>(
    // @ts-expect-error TS2345 - strict migration
    viewerCtx.currentPage?.id !== ROOT_SECTION_ID ? viewerCtx.currentPage : undefined,
  );
  const [selectedPageDepth, setSelectedPageDepth] = useState<number>();
  const [parentOfSelectedPage, setParentOfSelectedPage] = useState<ContentNode>();
  const [selectedPageIndex, setSelectedPageIndex] = useState<number>();

  useEffect(() => {
    setUpdatedContent(deepClone(viewerCtx.content));
    if (viewerCtx.currentPage?.id === ROOT_SECTION_ID) {
      // @ts-expect-error TS2345 - strict migration
      setSelectedPage(undefined);
    } else {
      // @ts-expect-error TS2345 - strict migration
      setSelectedPage(viewerCtx.currentPage);
    }
  }, [viewerCtx.content, viewerCtx.currentPage, viewerCtx.currentPage?.title]);

  useEffect(() => {
    if (selectedPage) {
      const parentInfo = findParentPage(updatedContent.contentData, selectedPage);

      if (!parentInfo) {
        throw new Error('Could not find parent page for selected page.');
      }

      setParentOfSelectedPage(parentInfo.parent);
      setSelectedPageIndex(parentInfo.pageIdx);
    }
  }, [selectedPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = () => {
    if (viewerCtx.updateContent) {
      viewerCtx.updateContent(updatedContent);
    }
  };

  const resetForm = () => {
    setUpdatedContent(deepClone(viewerCtx.content));
    setSelectedPage(
      // @ts-expect-error TS2345 - strict migration
      viewerCtx.currentPage?.id !== ROOT_SECTION_ID ? viewerCtx.currentPage : undefined,
    );
  };

  const movePageUp = () => {
    if (selectedPageIndex === 0) {
      // Move selected page up one level into grandparent
      // @ts-expect-error TS2345 - strict migration
      const parentInfo = findParentPage(updatedContent.contentData, parentOfSelectedPage);

      // already at top of root level
      if (!parentInfo) {
        return;
      }

      parentOfSelectedPage?.subContent.splice(selectedPageIndex, 1);
      parentInfo?.parent?.subContent.splice(parentInfo.pageIdx, 0, selectedPage);

      setParentOfSelectedPage(parentInfo?.parent);
      setSelectedPageIndex(parentInfo.pageIdx);
      // @ts-expect-error TS18048 - strict migration
      setSelectedPageDepth(selectedPageDepth - 1);
    } else {
      // Move selected page before predecessor sibling
      // @ts-expect-error TS18048 - strict migration
      const newIdx = selectedPageIndex - 1;
      if (parentOfSelectedPage?.subContent) {
        const predecessorSibling = parentOfSelectedPage.subContent[newIdx];
        parentOfSelectedPage.subContent[newIdx] =
          // @ts-expect-error TS2538 - strict migration
          parentOfSelectedPage.subContent[selectedPageIndex];
        // @ts-expect-error TS2538 - strict migration
        parentOfSelectedPage.subContent[selectedPageIndex] = predecessorSibling;
        setSelectedPageIndex(newIdx);
      }
    }

    setUpdatedContent(updatedContent);
  };

  const movePageDown = () => {
    // Move selected page behind next sibling
    if (parentOfSelectedPage?.subContent) {
      // @ts-expect-error TS18048 - strict migration
      const newIdx = Math.min(selectedPageIndex + 1, parentOfSelectedPage?.subContent.length - 1);
      const t = parentOfSelectedPage.subContent[newIdx];
      // @ts-expect-error TS2538 - strict migration
      parentOfSelectedPage.subContent[newIdx] = parentOfSelectedPage.subContent[selectedPageIndex];
      // @ts-expect-error TS2538 - strict migration
      parentOfSelectedPage.subContent[selectedPageIndex] = t;
      setSelectedPageIndex(newIdx);
      setUpdatedContent(updatedContent);
    }
  };

  const movePageInto = () => {
    if (parentOfSelectedPage?.subContent) {
      // @ts-expect-error TS18048 - strict migration
      if (selectedPageIndex >= parentOfSelectedPage?.subContent.length) {
        // At bottom of current level not suceeding page to move into
        return;
      }

      // Make selected page a child of the next sibling
      // @ts-expect-error TS18048 - strict migration
      const newParent = parentOfSelectedPage?.subContent[selectedPageIndex + 1];
      if (newParent?.subContent) {
        const newIdx = 0;
        newParent.subContent.splice(newIdx, 0, selectedPage);
        // @ts-expect-error TS2769 - strict migration
        parentOfSelectedPage.subContent?.splice(selectedPageIndex, 1);

        setSelectedPageIndex(newIdx);
        setParentOfSelectedPage(newParent);
        setUpdatedContent(updatedContent);
        // @ts-expect-error TS18048 - strict migration
        setSelectedPageDepth(selectedPageDepth + 1);
      }
    }
  };

  const handleSelectedPageChanged = (newSelectedPage: ContentNode, depth: number) => {
    const parentInfo = findParentPage(updatedContent.contentData, newSelectedPage);

    if (!parentInfo) {
      throw new Error('Could not find parent page for selected page.');
    }

    setSelectedPage(newSelectedPage);
    setParentOfSelectedPage(parentInfo.parent);
    setSelectedPageIndex(parentInfo.pageIdx);
    setSelectedPageDepth(depth);
  };

  return (
    <ModalDialogButtonV3
      iconName="add-plus"
      iconAlign="left"
      header={constants.REORDER_CONTENT_PAGES_HEADER}
      buttonText={constants.REORDER_CONTENT_PAGES_BUTTON_TEXT}
      submitButtonText={constants.REORDER_CONTENT_PAGES_SUBMIT_BUTTON_TEXT}
      onSubmit={apply}
      onDismiss={resetForm}
    >
      <p>{constants.REORDER_CONTENT_PAGES_INFO_TEXT}:</p>
      <Box margin={{ top: 's' }}>
        <div>
          <Box margin={{ top: 's', right: 'xs' }}>
            <Button iconName="caret-up-filled" disabled={!selectedPage} onClick={movePageUp}>
              {constants.REORDER_CONTENT_PAGES_MOVE_UP_BUTTON_TEXT}
            </Button>
            <Button iconName="caret-down-filled" disabled={!selectedPage} onClick={movePageDown}>
              {constants.REORDER_CONTENT_PAGES_MOVE_DOWN_BUTTON_TEXT}
            </Button>
            <Button
              iconName="caret-right-filled"
              // @ts-expect-error TS18048 - strict migration
              disabled={!selectedPage || selectedPageDepth >= 4}
              onClick={movePageInto}
            >
              {constants.REORDER_CONTENT_PAGES_MOVE_INTO_BUTTON_TEXT}
            </Button>
            <ContentPagesTree
              content={updatedContent}
              nodeClassName="parent-page-item"
              selectedNodeClassName="parent-page-selected"
              selectedNode={selectedPage}
              onSelectedNodeChanged={handleSelectedPageChanged}
              treeStatus={TreeStatus.ExpandAll}
            />
          </Box>
        </div>
      </Box>
    </ModalDialogButtonV3>
  );
};
